import { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { StatusBanner } from '../../components/StatusBanner';
import { Spinner } from '../../components/Spinner';
import { DateField } from '../../components/DateField';
import {
  DOCUMENT_TYPE_LABEL,
  REQUIRED_DOCUMENT_TYPES,
  type DocumentDoc,
  type DocumentType,
} from '../../lib/types';
import { DEMO_DOCUMENTS, DEMO_UIDS } from '../../lib/demoData';
import { soloGateApplies, SHOW_SOLO_NOTICE_TO_PILOTS } from '../../lib/soloGate';
import { FilePicker } from '../../components/FilePicker';

// Renter's insurance isn't one of the four documents onboarding requires (it
// has its own step, which a pilot may skip for 14 days), but a renewed
// certificate has to land somewhere — the renewal email sends pilots here.
const TYPE_OPTIONS: DocumentType[] = [
  ...REQUIRED_DOCUMENT_TYPES,
  'renters_insurance',
  'other',
];

export function Documents() {
  const { fbUser, profile, demoMode } = useAuth();
  const [docs, setDocs] = useState<DocumentDoc[] | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (demoMode) {
      // In demo mode we show Brian's verified docs for the active lessee preview,
      // or empty for incomplete/pending so the missing-docs banner shows.
      const isActive = profile?.status === 'active';
      setDocs(isActive ? DEMO_DOCUMENTS.filter((d) => d.ownerUid === DEMO_UIDS.brian) : []);
      return;
    }
    if (!db || !fbUser) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'documents'),
        where('ownerUid', '==', fbUser.uid),
        orderBy('uploadedAt', 'desc')
      ),
      (snap) => setDocs(snap.docs.map((d) => ({ ...(d.data() as DocumentDoc), documentId: d.id })))
    );
    return () => unsub();
  }, [fbUser, demoMode, profile?.status]);

  const verifiedByType = new Map<DocumentType, DocumentDoc>();
  const pendingByType = new Map<DocumentType, DocumentDoc>();
  (docs ?? []).forEach((d) => {
    if (d.verificationStatus === 'verified') verifiedByType.set(d.type, d);
    if (d.verificationStatus === 'pending') pendingByType.set(d.type, d);
  });
  // Open the upload form on what's actually missing, not on a document that's
  // already verified.
  const defaultUploadType: DocumentType =
    TYPE_OPTIONS.find((t) => t !== 'other' && !verifiedByType.has(t) && !pendingByType.has(t)) ?? 'renters_insurance';

  const missingRequired = REQUIRED_DOCUMENT_TYPES.filter(
    (t) => !verifiedByType.has(t) && !pendingByType.has(t)
  );

  const lockedView = profile?.status === 'pending_approval';
  // A pilot whose paperwork is complete shouldn't land on a page that opens with
  // a to-do. Lead with confirmation and keep the upload form one tap away.
  const allComplete = !lockedView && missingRequired.length === 0 && (docs?.length ?? 0) > 0;
  // Insurance is deliberately not a REQUIRED_DOCUMENT_TYPE — §11.2 lets a pilot fly
  // dual while the policy is written, so gating onboarding on it would block the path
  // the lease provides. But "You're all set" is untrue while it's missing.
  const needsInsurance = allComplete && soloGateApplies(profile);

  return (
    <>
      <PageHeader
        title="Documents"
        subtitle={
          allComplete
            ? 'Your paperwork on file with AJM.'
            : 'Upload required documents. Each must include an expiration date where applicable.'
        }
      />

      {lockedView && (
        <StatusBanner tone="warning" title="Awaiting approval">
          Your registration is pending. You can upload documents once approved.
        </StatusBanner>
      )}

      {!lockedView && missingRequired.length > 0 && (
        <StatusBanner tone="warning" title={`${missingRequired.length} required document(s) missing`}>
          {missingRequired.map((t) => DOCUMENT_TYPE_LABEL[t]).join(', ')}
        </StatusBanner>
      )}

      {allComplete && !needsInsurance && (
        <StatusBanner tone="success" title="You're all set">
          Every required document is on file and verified. Nothing needed from you.
        </StatusBanner>
      )}

      {SHOW_SOLO_NOTICE_TO_PILOTS && needsInsurance && (
        <StatusBanner tone="info" title="You're set to fly dual">
          Everything required to onboard is on file and verified. One more before you fly as sole pilot in
          command: your <strong>certificate of insurance</strong>. Lease §11.2 gives you 14 days from the
          effective date, and until it&rsquo;s on file you fly only with an instructor AJM has approved.
        </StatusBanner>
      )}

      {!lockedView &&
        (allComplete ? (
          showUpload ? (
            <UploadCard key={defaultUploadType} defaultType={defaultUploadType} />
          ) : (
            <button type="button" className="btn-secondary mt-4" onClick={() => setShowUpload(true)}>
              Upload or replace a document
            </button>
          )
        ) : (
          <UploadCard key={defaultUploadType} defaultType={defaultUploadType} />
        ))}

      <div className="mt-6">
        <h2 className="font-display text-lg font-semibold text-navy-900 mb-3">Your documents</h2>
        {!docs ? (
          <Spinner label="Loading documents" />
        ) : docs.length === 0 ? (
          <div className="card p-8 text-center text-ink-muted">No documents uploaded yet.</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 sm:hidden">
              {docs.map((d) => (
                <div key={d.documentId} className="card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-navy-900">{DOCUMENT_TYPE_LABEL[d.type]}</div>
                    <a href={d.fileRef} target="_blank" rel="noopener" className="shrink-0 text-sm font-medium text-navy-700 underline">
                      View
                    </a>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
                    <DocBadge status={d.verificationStatus} />
                    <span>Expires {d.expirationDate || '—'}</span>
                    <span>Uploaded {uploadedDate(d)}</span>
                  </div>
                  {d.verificationStatus === 'rejected' && d.rejectionReason && (
                    <div className="mt-1.5 text-xs text-red-700">{d.rejectionReason}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Table for wider screens */}
            <div className="card hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead className="bg-sky-50 text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    <th className="px-4 py-3 font-medium">Uploaded</th>
                    <th className="px-4 py-3 font-medium text-right">File</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {docs.map((d) => (
                    <tr key={d.documentId}>
                      <td className="px-4 py-3 whitespace-nowrap">{DOCUMENT_TYPE_LABEL[d.type]}</td>
                      <td className="px-4 py-3">
                        <DocBadge status={d.verificationStatus} />
                        {d.verificationStatus === 'rejected' && d.rejectionReason && (
                          <div className="text-xs text-red-700 mt-1">{d.rejectionReason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{d.expirationDate || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{uploadedDate(d)}</td>
                      <td className="px-4 py-3 text-right">
                        <a href={d.fileRef} target="_blank" rel="noopener" className="text-navy-700 underline">
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function uploadedDate(d: DocumentDoc): string {
  return d.uploadedAt && 'toDate' in d.uploadedAt
    ? (d.uploadedAt as { toDate(): Date }).toDate().toLocaleDateString()
    : '—';
}

function DocBadge({ status }: { status: DocumentDoc['verificationStatus'] }) {
  return (
    <span className={status === 'verified' ? 'badge-success' : status === 'rejected' ? 'badge-danger' : 'badge-warning'}>
      {status}
    </span>
  );
}

function UploadCard({ defaultType }: { defaultType: DocumentType }) {
  const { fbUser } = useAuth();
  const fileRefInput = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<DocumentType>(defaultType);
  const [exp, setExp] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function onUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!fbUser || !db || !storage) return;
    const file = fileRefInput.current?.files?.[0];
    if (!file) {
      setMsg({ kind: 'err', text: 'Choose a file to upload.' });
      return;
    }
    if (type === 'renters_insurance' && !exp) {
      setMsg({ kind: 'err', text: 'Enter the policy’s expiration date — it’s on the certificate.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const path = `documents/${fbUser.uid}/${Date.now()}-${file.name}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);

      const docCol = collection(db, 'documents');
      const created = await addDoc(docCol, {
        ownerUid: fbUser.uid,
        type,
        fileRef: url,
        fileName: file.name,
        expirationDate: exp || null,
        verificationStatus: 'pending',
        uploadedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'documents', created.id), { documentId: created.id });
      // The insurance date on the pilot's profile is the one the portal and the
      // renewal reminders read (it beats a document date), so a renewed
      // certificate has to move it too.
      if (type === 'renters_insurance') {
        await updateDoc(doc(db, 'users', fbUser.uid), { 'insurance.expirationDate': exp });
      }
      setMsg({ kind: 'ok', text: 'Uploaded. The owner will verify it.' });
      if (fileRefInput.current) fileRefInput.current.value = '';
      setExp('');
    } catch (e) {
      setMsg({
        kind: 'err',
        text: e instanceof Error ? e.message : 'Upload failed.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onUpload} className="card p-5 mt-4">
      <h2 className="font-display text-lg font-semibold text-navy-900 mb-3">Upload a document</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Document type</label>
          <select
            className="input"
            value={type}
            onChange={(e) => {
              const t = e.target.value as DocumentType;
              setType(t);
              if (t === 'pilot_certificate') setExp('');
            }}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {DOCUMENT_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        {type === 'pilot_certificate' ? (
          <div />
        ) : (
          <div>
            <label className="label">
              {type === 'renters_insurance' ? 'Policy expiration' : 'Expiration date (if applicable)'}
            </label>
            <DateField value={exp} onChange={(e) => setExp(e.target.value)} />
          </div>
        )}
        <div>
          <label className="label">File (PDF or image, max 15MB)</label>
          <FilePicker ref={fileRefInput} accept="image/*,application/pdf" />
        </div>
      </div>
      {msg && (
        <div
          role="alert"
          className={`mt-3 text-sm rounded-md px-3 py-2 ${
            msg.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  );
}
